import { Box, Tab, Tabs } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StudioSettingsDataTab,
  StudioSettingsDetailsTab,
  StudioSettingsFormatTab,
  StudioSettingsInteractionsTab,
} from '.';
import { useStudio, type StudioItem } from '../../context';
import { settingsTabTranslationKeys, settingsTabs, type SettingsTabName } from '../../layout/utils';
import {
  StudioSidebarContainer,
  StudioSidebarScrollable,
  StudioSidebarScrollableInterior,
} from '../lib';

// Text is left out for now: a bare tappable label reads as a link, not a button.
const INTERACTIVE_TYPES: StudioItem['type'][] = ['circle', 'image', 'rect', 'triangle'];

export const StudioSettings = () => {
  const { t } = useTranslation();
  const { activeSettingsTab, selectedItems, setActiveSettingsTab } = useStudio();

  const displayTabs = useMemo(() => {
    const firstItem = selectedItems.at(0);
    if (!firstItem) return [];

    return settingsTabs.filter((tab) => {
      if (firstItem.type !== 'text' && tab === 'Data Source') return false;
      if (!['image', 'video'].includes(firstItem.type) && tab === 'Details') return false;
      if (!INTERACTIVE_TYPES.includes(firstItem.type) && tab === 'Interactions') return false;
      return true;
    });
  }, [selectedItems]);

  if (selectedItems.length !== 1) return null;
  const selectedItem = selectedItems.at(0);
  if (!selectedItem) return null;

  const translateTab = (tab: SettingsTabName) => {
    const key = settingsTabTranslationKeys[tab];
    return t(`pages.studio.edit.settings.tabs.${key}`);
  };

  return (
    <StudioSidebarContainer>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Tabs
          value={activeSettingsTab}
          sx={{
            padding: (theme) => theme.spacing(1, 1, 0, 1),
          }}
        >
          {displayTabs.map((name) => (
            <Tab
              key={name}
              label={translateTab(name)}
              onClick={() => setActiveSettingsTab(name)}
              value={name}
            />
          ))}
        </Tabs>
        <StudioSidebarScrollable>
          <StudioSidebarScrollableInterior>
            {activeSettingsTab === 'Settings' && (
              <StudioSettingsFormatTab selectedItem={selectedItem} />
            )}
            {activeSettingsTab === 'Data Source' && selectedItem.type === 'text' && (
              <StudioSettingsDataTab selectedItem={selectedItem} />
            )}
            {activeSettingsTab === 'Details' &&
              (selectedItem.type === 'image' || selectedItem.type === 'video') && (
                <StudioSettingsDetailsTab selectedItem={selectedItem} />
              )}
            {activeSettingsTab === 'Interactions' &&
              INTERACTIVE_TYPES.includes(selectedItem.type) && (
                <StudioSettingsInteractionsTab selectedItem={selectedItem} />
              )}
          </StudioSidebarScrollableInterior>
        </StudioSidebarScrollable>
      </Box>
    </StudioSidebarContainer>
  );
};
